import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useParams } from "react-router-dom";
import { organization, useListOrganizations } from "../lib/auth-client.ts";
import { STORAGE_KEYS, slugify } from "@currencycore/shared";
import { toast } from "../components/Toast.tsx";
import { getOrgUsage } from "../lib/api.ts";
import { canCreateOrg, deleteOrganization, leaveOrganization, type MemberRole } from "../lib/team.ts";
import { trackOrgSwitch, trackOrgCreate } from "../lib/analytics.ts";

// ---------------------------------------------------------------------------
// Create org modal
// ---------------------------------------------------------------------------

function CreateOrgModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string, name: string) => void }) {
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Stable random suffix generated once per modal mount so the slug stays
  // consistent across keystrokes and stays unique on the DB unique constraint.
  const [suffix] = useState(() => Math.random().toString(36).slice(2, 8));

  const slug = name ? `${slugify(name)}-${suffix}` : "";

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setLoading(true);
    setError(null);
    try {
      const res = await organization.create({ name: trimmed, slug });
      if (res?.error) { setError(res.error.message ?? "Could not create organization."); return; }
      const id = res?.data?.id;
      if (!id) throw new Error("Could not create organization.");
      onCreated(id, trimmed);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create organization.");
    } finally {
      setLoading(false);
    }
  }

  return createPortal(
    <div
      className="modal-root open"
      style={{ position: "fixed", inset: 0, zIndex: 100, display: "block" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="modal-scrim" style={{ position: "absolute", inset: 0, background: "oklch(8% 0.01 235 / 0.6)", backdropFilter: "blur(3px)" }} />
      <div className="modal" role="dialog" aria-modal="true" style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", opacity: 1 }}>
        <div className="modal-head">
          <div>
            <h3>Create organization</h3>
            <p className="sub">You'll be the owner. You can invite teammates after.</p>
          </div>
          <button className="btn btn-icon btn-ghost" onClick={onClose} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        {error && <div className="error-banner">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="field">
              <label htmlFor="orgName">Organization name</label>
              <input
                className="input"
                id="orgName"
                required
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Acme Inc."
                autoComplete="off"
              />
            </div>
            <div className="field">
              <label>Slug</label>
              <input className="input mono" value={slug} readOnly placeholder="acme-inc-a1b2c3" />
              <span className="help">Auto-generated, unique handle for this organization.</span>
            </div>
          </div>
          <div className="modal-foot">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <><span className="spinner" />Creating…</> : "Create organization"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Org card styles (inline since they're page-specific)
// ---------------------------------------------------------------------------

const orgGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  gap: "16px",
};

const orgCardStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  padding: "20px",
  transition: "border-color .15s",
};

// ---------------------------------------------------------------------------
// Delete / leave confirmation modal
// ---------------------------------------------------------------------------

function ConfirmActionModal({
  org,
  mode,
  onClose,
  onDone,
}: {
  org: { id: string; name: string };
  mode: "delete" | "leave";
  onClose: () => void;
  onDone: (id: string) => void;
}) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isDelete = mode === "delete";
  const canDo = isDelete ? text.trim().toLowerCase() === "delete" : true;

  async function run() {
    setLoading(true);
    setError(null);
    try {
      if (isDelete) await deleteOrganization(org.id);
      else await leaveOrganization(org.id);
      toast(isDelete ? `Deleted ${org.name}` : `Left ${org.name}`);
      onDone(org.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Action failed.");
      setLoading(false);
    }
  }

  return createPortal(
    <div
      className="modal-root open"
      style={{ position: "fixed", inset: 0, zIndex: 100, display: "block" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="modal-scrim" style={{ position: "absolute", inset: 0, background: "oklch(8% 0.01 235 / 0.6)", backdropFilter: "blur(3px)" }} />
      <div className="modal" role="dialog" aria-modal="true" style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", opacity: 1 }}>
        <div className="modal-head">
          <div>
            <h3 style={isDelete ? { color: "var(--danger)" } : undefined}>{isDelete ? `Delete ${org.name}` : `Leave ${org.name}`}</h3>
            <p className="sub">
              {isDelete
                ? "This permanently deletes the organization, its API keys, members and invoices for everyone."
                : "You'll lose access to this organization's data and API keys."}
            </p>
          </div>
          <button className="btn btn-icon btn-ghost" onClick={onClose} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
        </div>
        {error && <div className="error-banner">{error}</div>}
        {isDelete && (
          <div className="modal-body">
            <div className="field">
              <label htmlFor="confirmOrg">Type <strong style={{ color: "var(--text)" }}>delete</strong> to confirm</label>
              <input id="confirmOrg" className="input" value={text} onChange={(e) => setText(e.target.value)} placeholder="delete" autoFocus autoComplete="off" />
            </div>
          </div>
        )}
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className={`btn ${isDelete ? "btn-danger" : "btn-primary"}`} disabled={!canDo || loading} onClick={run}>
            {loading ? <><span className="spinner" />{isDelete ? "Deleting…" : "Leaving…"}</> : isDelete ? "Delete organization" : "Leave organization"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function OrganizationsPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  // useListOrganizations is the single source of truth — it returns the mock
  // orgs in mock mode and the real org list otherwise.
  const { data: orgsData } = useListOrganizations();
  const orgs = orgsData ?? [];
  const [showCreate, setShowCreate] = useState(false);
  const [confirm, setConfirm] = useState<{ id: string; name: string; mode: "delete" | "leave" } | null>(null);
  const eligible = canCreateOrg();

  // The Better-Auth org object carries no plan field, so the card's plan badge
  // and usage % must come from the authoritative API. Fetch each org's real
  // usage stats and key the derived display values by org id.
  const [orgStats, setOrgStats] = useState<Record<string, { planLabel: string; usagePct: string }>>({});
  const orgIdsKey = orgs.map((o) => o.id).join(",");
  useEffect(() => {
    let active = true;
    Promise.all(
      orgs.map((o) =>
        getOrgUsage(o.id)
          .then((u) => {
            const lim = u.effectiveLimit;
            const usagePct =
              lim === null || lim === 0
                ? lim === null
                  ? "—"
                  : "0%"
                : `${Math.min(100, Math.round((u.requests / lim) * 100))}%`;
            return [o.id, { planLabel: u.planLabel, usagePct }] as const;
          })
          .catch(() => null),
      ),
    ).then((entries) => {
      if (!active) return;
      const next: Record<string, { planLabel: string; usagePct: string }> = {};
      for (const e of entries) if (e) next[e[0]] = e[1];
      setOrgStats(next);
    });
    return () => {
      active = false;
    };
    // Re-run when the set of org ids changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgIdsKey]);

  function afterRemoval(removedId: string) {
    const remaining = orgs.filter((o) => o.id !== removedId);
    const next = remaining.find((o) => o.id === orgId)?.id ?? remaining[0]?.id;
    if (next) {
      localStorage.setItem(STORAGE_KEYS.lastOrgId, next);
      navigate(`/dashboard/${next}/organizations`, { replace: true });
    } else {
      localStorage.removeItem(STORAGE_KEYS.lastOrgId);
      navigate("/dashboard", { replace: true });
    }
  }

  function switchOrg(id: string) {
    localStorage.setItem(STORAGE_KEYS.lastOrgId, id);
    trackOrgSwitch(id);
    navigate(`/dashboard/${id}`, { replace: true });
    toast(`Switched workspace`, true);
  }

  function handleCreated(id: string, name: string) {
    setShowCreate(false);
    localStorage.setItem(STORAGE_KEYS.lastOrgId, id);
    trackOrgCreate();
    toast(`Created "${name}"`, true);
    navigate(`/dashboard/${id}`, { replace: true });
  }

  function openCreate() {
    if (!eligible) {
      toast("Upgrade an organization you own to a paid plan to create more");
      return;
    }
    setShowCreate(true);
  }

  return (
    <div className="animate-fade-in">
      {/* Page header */}
      <div className="page-head rise">
        <div>
          <h1>Organizations</h1>
          <p className="sub">
            You belong to <strong style={{ color: "var(--text)" }}>{orgs.length} organization{orgs.length !== 1 ? "s" : ""}</strong>.
            Switch between them or create a new one.
          </p>
        </div>
        <div className="page-head-actions">
          <button className="btn btn-primary" onClick={openCreate} disabled={!eligible} title={eligible ? undefined : "Upgrade an organization you own to a paid plan to create more"}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Create organization
          </button>
        </div>
      </div>

      {!eligible && (
        <div className="notice rise" style={{ marginBottom: "16px" }}>
          <span className="ic">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 8v4M12 16h.01" /></svg>
          </span>
          You can join unlimited organizations, but creating a new one requires at least one paid organization you own.{" "}
          <Link to={`/dashboard/${orgId}/billing`} style={{ color: "var(--accent-text)" }}>Upgrade a plan</Link> to unlock org creation.
        </div>
      )}

      {/* Org grid */}
      <div style={orgGridStyle} className="rise">
        {orgs.map((org) => {
          const isActive = org.id === orgId;
          const letter = org.name[0]?.toUpperCase() ?? "?";
          const role = (org as { role?: MemberRole }).role ?? "owner";
          // Real plan label + usage from the API (dashes until they load).
          const stats = orgStats[org.id];
          const planLabel = stats?.planLabel ?? "—";
          const usagePct = stats?.usagePct ?? "—";
          return (
            <div
              key={org.id}
              className={`card${isActive ? "" : ""}`}
              style={{
                ...orgCardStyle,
                borderColor: isActive ? "var(--accent-line)" : undefined,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "13px" }}>
                <span style={{
                  width: "46px", height: "46px", borderRadius: "12px", flexShrink: 0,
                  display: "grid", placeItems: "center",
                  fontFamily: "var(--font-display)", fontWeight: 600, fontSize: "18px",
                  background: isActive ? "var(--accent-soft)" : "var(--surface-2)",
                  color: isActive ? "var(--accent-text)" : "var(--text-muted)",
                  border: `1px solid ${isActive ? "var(--accent-line)" : "var(--border)"}`,
                }}>
                  {letter}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="row gap-sm">
                    <strong style={{ fontSize: "16px", fontFamily: "var(--font-display)" }}>{org.name}</strong>
                    {isActive && <span className="badge badge-accent" style={{ fontSize: "10.5px" }}>Current</span>}
                  </div>
                  <div className="muted mono" style={{ fontSize: "11.5px" }}>{org.id}</div>
                </div>
                <span className={`badge ${role === "owner" ? "badge-accent" : "badge-neutral"}`} style={{ textTransform: "capitalize" }}>{role}</span>
              </div>

              {/* Meta grid */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "10px" }}>
                {[
                  { lbl: "Plan", num: planLabel },
                  { lbl: "Members", num: "3" },
                  { lbl: "Usage", num: usagePct },
                ].map(({ lbl, num }) => (
                  <div key={lbl} style={{
                    background: "var(--bg)", border: "1px solid var(--border-soft)",
                    borderRadius: "var(--radius-xs)", padding: "10px 12px",
                  }}>
                    <div style={{ fontSize: "10.5px", textTransform: "uppercase", letterSpacing: ".1em", color: "var(--text-faint)" }}>{lbl}</div>
                    <div style={{ fontFamily: "var(--font-display)", fontSize: "18px", fontWeight: 600, fontVariantNumeric: "tabular-nums", marginTop: "2px" }}>{num}</div>
                  </div>
                ))}
              </div>

              <div className="row gap-sm">
                {isActive ? (
                  <button className="btn" style={{ flex: 1, justifyContent: "center" }} disabled>
                    Active workspace
                  </button>
                ) : (
                  <button
                    className="btn btn-primary"
                    style={{ flex: 1, justifyContent: "center" }}
                    onClick={() => switchOrg(org.id)}
                  >
                    Switch to
                  </button>
                )}
                {role === "owner" ? (
                  <button className="btn btn-danger" onClick={() => setConfirm({ id: org.id, name: org.name, mode: "delete" })}>
                    Delete
                  </button>
                ) : (
                  <button className="btn btn-ghost" onClick={() => setConfirm({ id: org.id, name: org.name, mode: "leave" })}>
                    Leave
                  </button>
                )}
              </div>
            </div>
          );
        })}

        {/* Create card */}
        <button
          className="card rise"
          style={{
            ...orgCardStyle,
            borderStyle: "dashed",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
            minHeight: "180px",
            color: "var(--text-subtle)",
          }}
          onClick={openCreate}
        >
          <span style={{
            width: "46px", height: "46px", borderRadius: "12px",
            display: "grid", placeItems: "center",
            background: "var(--accent-soft)", color: "var(--accent-text)",
            border: "1px solid var(--accent-line)",
          }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M12 5v14M5 12h14" />
            </svg>
          </span>
          <strong style={{ fontFamily: "var(--font-display)", color: "var(--text)" }}>Create organization</strong>
          <span className="muted" style={{ fontSize: "12.5px" }}>A new workspace with its own keys, team &amp; billing</span>
        </button>
      </div>

      {/* Info notice */}
      <div className="notice mt-lg rise">
        <span className="ic">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9" /><path d="M12 8v4M12 16h.01" />
          </svg>
        </span>
        Each organization has separate API keys, usage limits, members and invoices. Members and billing don't carry over between organizations.
      </div>

      {/* Modal */}
      {showCreate && (
        <CreateOrgModal
          onClose={() => setShowCreate(false)}
          onCreated={handleCreated}
        />
      )}

      {confirm && (
        <ConfirmActionModal
          org={{ id: confirm.id, name: confirm.name }}
          mode={confirm.mode}
          onClose={() => setConfirm(null)}
          onDone={(id) => { setConfirm(null); afterRemoval(id); }}
        />
      )}
    </div>
  );
}
